import {
  Counters,
  FormState,
  GenerateErrorsResponse,
  GenericFormData,
  RepeatedValidationFieldConfig,
  SimpleValidationFieldConfig,
  ValidationConfig,
  ValidationError,
  ValidationErrors,
  ValidationFieldConfig,
  ValidationMode,
} from './types.js';

function fieldIsPresent<FormData extends GenericFormData>(
  data: FormData,
  field: ValidationFieldConfig<FormData>,
  key: keyof FormData,
): boolean {
  if (field.type === 'simple' && field.fieldIsPresent) return field.fieldIsPresent(data);
  const value = data[key];
  if (Array.isArray(value)) return value.length > 0;
  return !!value || value === false || (typeof value === 'number' && value === 0);
}

export function isValid<FormData extends GenericFormData>(
  formData: FormData,
  validation: ValidationConfig<FormData>,
  errors: ValidationErrors<FormData>,
): boolean {
  return Object.entries(validation).every(([key, field]) => {
    if (field.condition && !field.condition(formData)) return true;
    if (field.type !== 'repeated') {
      if (field.required && !fieldIsPresent(formData, field, key)) return false;
      return !errors?.[key];
    }
    return (
      (!field.required || formData[key].length > 0) &&
      formData[key].every((val, idx) =>
        isValid(
          val,
          field.schema,
          ((errors?.[key] ?? [])[idx] ?? {}) as ValidationErrors<FormData[typeof key][number]>,
        ),
      )
    );
  });
}

const EMPTY_COUNTERS = Object.freeze({ missingRequiredFields: 0, requiredFields: 0 });

function isFieldConditionEnabled<FormData extends GenericFormData>(
  field: ValidationFieldConfig<FormData>,
  formData: FormData,
): boolean {
  return !field.condition || field.condition(formData);
}

function validateSimpleField<FormData extends GenericFormData>(
  { formData, touchedFields }: FormState<FormData>,
  field: SimpleValidationFieldConfig<FormData>,
  validationMode: ValidationMode,
  hasSubmittedForm: boolean,
  key: keyof FormData,
): { errors?: ValidationError<FormData>; counters: Counters } {
  if (!isFieldConditionEnabled(field, formData)) return { counters: EMPTY_COUNTERS };

  const valuePresent = !!fieldIsPresent(formData, field, key);
  let errors =
    (valuePresent || (field.required && touchedFields[key])) &&
    field.validate &&
    field.validate(formData);

  const checkOnSubmitMode =
    [ValidationMode.RequiredOnSubmit, ValidationMode.OnSubmit].includes(validationMode) &&
    hasSubmittedForm;
  const missingRequiredValue = field.required && !valuePresent;
  if (checkOnSubmitMode && missingRequiredValue)
    errors = errors || (field.requiredErrorMessage ?? 'Field is required');

  const checkOnEditMode = validationMode === ValidationMode.OnEdit && touchedFields[key];

  if (Array.isArray(errors)) {
    errors = errors.length > 0 ? errors : undefined;
  }

  return {
    errors,
    counters: {
      requiredFields: field.required ? 1 : 0,
      missingRequiredFields: (checkOnEditMode || checkOnSubmitMode) && missingRequiredValue ? 1 : 0,
    },
  };
}

function validateRepeatedField<FormData extends GenericFormData>(
  { formData, touchedFields }: FormState<FormData>,
  field: RepeatedValidationFieldConfig<FormData>,
  validationMode: ValidationMode,
  hasSubmittedForm: boolean,
  key: keyof FormData,
): { errors?: ValidationError<FormData>; counters: Counters } {
  return formData[key]?.reduce(
    ({ errors: repeatedErrors, counters: repeatedCounters }, val, idx) => {
      const { errors: valErrors, counters: valCounters } = isFieldConditionEnabled(field, formData)
        ? generateErrors(
            { formData: val, touchedFields: touchedFields[key]?.[idx] ?? {} },
            field.schema,
            hasSubmittedForm,
            validationMode,
          )
        : { errors: {}, counters: EMPTY_COUNTERS };
      return {
        errors: [...repeatedErrors, valErrors],
        counters: {
          missingRequiredFields:
            repeatedCounters.missingRequiredFields + valCounters.missingRequiredFields,
          requiredFields: repeatedCounters.requiredFields + valCounters.requiredFields,
        },
      };
    },
    { errors: [], counters: EMPTY_COUNTERS } as GenerateErrorsResponse<FormData>,
  );
}

export function generateErrors<FormData extends GenericFormData>(
  formState: FormState<FormData>,
  validation: ValidationConfig<FormData>,
  hasSubmittedForm = false,
  validationMode = ValidationMode.OnEdit,
): GenerateErrorsResponse<FormData> {
  if (ValidationMode.OnSubmit === validationMode && !hasSubmittedForm)
    return { errors: {}, counters: EMPTY_COUNTERS };

  return Object.entries(validation).reduce(
    ({ errors, counters }, [key, field]) => {
      const { errors: fieldErrors, counters: fieldCounters } =
        field.type === 'repeated'
          ? validateRepeatedField(
              formState,
              field as RepeatedValidationFieldConfig<FormData>,
              validationMode,
              hasSubmittedForm,
              key,
            )
          : validateSimpleField(
              formState,
              field as SimpleValidationFieldConfig<FormData>,
              validationMode,
              hasSubmittedForm,
              key,
            );

      return {
        errors: { ...errors, ...(fieldErrors && { [key]: fieldErrors }) },
        counters: {
          missingRequiredFields:
            counters.missingRequiredFields + fieldCounters.missingRequiredFields,
          requiredFields: counters.requiredFields + fieldCounters.requiredFields,
        },
      };
    },
    { errors: {}, counters: EMPTY_COUNTERS } as GenerateErrorsResponse<FormData>,
  );
}
